import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  TextField,
  Avatar,
  Divider,
  Switch,
  FormControlLabel,
  Alert
} from '@mui/material';
import {
  Person,
  Email,
  Phone,
  Security,
  Notifications,
  Save,
  Edit
} from '@mui/icons-material';
import MainLayout from '../components/MainLayout';
import { useFeedback } from '../components/EnhancedFeedbackProvider';

const Profile = () => {
  const { showSuccess, showError } = useFeedback();
  const [isEditing, setIsEditing] = useState(false);
  const [profileData, setProfileData] = useState({
    firstName: 'Dr. Admin',
    lastName: 'Medical',
    email: 'admin@medical.com',
    phone: '+33 1 23 45 67 89',
    role: 'Médecin',
    department: 'Médecine Générale',
    notifications: {
      email: true,
      sms: false,
      push: true
    }
  });

  const handleInputChange = (field, value) => {
    setProfileData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNotificationChange = (type, value) => {
    setProfileData(prev => ({
      ...prev,
      notifications: {
        ...prev.notifications,
        [type]: value
      }
    }));
  };

  const handleSave = () => {
    // Simulation de sauvegarde
    showSuccess('Profil mis à jour avec succès');
    setIsEditing(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
    // Recharger les données originales si nécessaire
  };

  return (
    <MainLayout>
      <Box>
        {/* En-tête */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1" fontWeight="bold">
            Mon Profil
          </Typography>
          <Box>
            {isEditing ? (
              <Box display="flex" gap={2}>
                <Button
                  variant="outlined"
                  onClick={handleCancel}
                >
                  Annuler
                </Button>
                <Button
                  variant="contained"
                  startIcon={<Save />}
                  onClick={handleSave}
                >
                  Sauvegarder
                </Button>
              </Box>
            ) : (
              <Button
                variant="contained"
                startIcon={<Edit />}
                onClick={() => setIsEditing(true)}
              >
                Modifier
              </Button>
            )}
          </Box>
        </Box>

        <Grid container spacing={3}>
          {/* Informations personnelles */}
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Informations Personnelles
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Prénom"
                      value={profileData.firstName}
                      onChange={(e) => handleInputChange('firstName', e.target.value)}
                      disabled={!isEditing}
                      InputProps={{
                        startAdornment: <Person sx={{ mr: 1, color: 'text.secondary' }} />
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Nom"
                      value={profileData.lastName}
                      onChange={(e) => handleInputChange('lastName', e.target.value)}
                      disabled={!isEditing}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Email"
                      type="email"
                      value={profileData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      disabled={!isEditing}
                      InputProps={{
                        startAdornment: <Email sx={{ mr: 1, color: 'text.secondary' }} />
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Téléphone"
                      value={profileData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      disabled={!isEditing}
                      InputProps={{
                        startAdornment: <Phone sx={{ mr: 1, color: 'text.secondary' }} />
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Rôle"
                      value={profileData.role}
                      disabled
                      InputProps={{
                        startAdornment: <Security sx={{ mr: 1, color: 'text.secondary' }} />
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Département"
                      value={profileData.department}
                      disabled
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Photo de profil et informations rapides */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Box display="flex" flexDirection="column" alignItems="center" textAlign="center">
                  <Avatar
                    sx={{
                      width: 120,
                      height: 120,
                      mb: 2,
                      bgcolor: 'primary.main',
                      fontSize: '2rem'
                    }}
                  >
                    {profileData.firstName.charAt(0)}{profileData.lastName.charAt(0)}
                  </Avatar>
                  
                  <Typography variant="h6" fontWeight={600}>
                    {profileData.firstName} {profileData.lastName}
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {profileData.role}
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary">
                    {profileData.department}
                  </Typography>
                </Box>
              </CardContent>
            </Card>

            {/* Notifications */}
            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Notifications
                </Typography>
                
                <Box display="flex" flexDirection="column" gap={2}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={profileData.notifications.email}
                        onChange={(e) => handleNotificationChange('email', e.target.checked)}
                        disabled={!isEditing}
                      />
                    }
                    label="Notifications par email"
                  />
                  
                  <FormControlLabel
                    control={
                      <Switch
                        checked={profileData.notifications.sms}
                        onChange={(e) => handleNotificationChange('sms', e.target.checked)}
                        disabled={!isEditing}
                      />
                    }
                    label="Notifications SMS"
                  />
                  
                  <FormControlLabel
                    control={
                      <Switch
                        checked={profileData.notifications.push}
                        onChange={(e) => handleNotificationChange('push', e.target.checked)}
                        disabled={!isEditing}
                      />
                    }
                    label="Notifications push"
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Informations de sécurité */}
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Sécurité
            </Typography>
            
            <Alert severity="info" sx={{ mb: 2 }}>
              Pour des raisons de sécurité, la modification du mot de passe doit être effectuée par un administrateur.
            </Alert>
            
            <Box display="flex" gap={2}>
              <Button
                variant="outlined"
                onClick={() => showError('Fonctionnalité de changement de mot de passe à implémenter')}
              >
                Changer le mot de passe
              </Button>
              
              <Button
                variant="outlined"
                color="warning"
                onClick={() => showError('Fonctionnalité de déconnexion de tous les appareils à implémenter')}
              >
                Déconnexion de tous les appareils
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </MainLayout>
  );
};

export default Profile;
